/**
 * useCalendarioData — hook che incapsula state + fetch + handlers per
 * `PaginaCalendario`. Tre viste supportate: 'mese' / 'trimestre' / 'settimana'.
 *
 * Espone:
 *   - state: year, month, view, isoWeek, weekOffset, data (mese), trimData,
 *     weekData, loading, todayIso, quarterMonths
 *   - navigation: prev(), next(), goToday(), setView()
 *   - mutations:
 *       handleToggleMit(id, fatto)         — optimistic update Mese
 *       handleToggleAzione(id, fatto)      — reload semplice (Mese + Settimana)
 *       reassignMitMese(id, currentData, newData) — drag&drop MIT in Mese
 *   - load(): re-fetch della vista corrente
 *
 * Refactor sessione 52: estratto da PaginaCalendario.jsx (484 righe → 80).
 */
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  fetchCalendario, fetchPeriodo, toggleMit, toggleAzione,
  applyOperations, invalidateCache, setRecurrenceOverride,
} from '../../lib/api';
import { playCheckSound } from '../../lib/sound';
import { VIEWS } from '../../lib/calendarConstants';

export function useCalendarioData() {
  const today = new Date();
  const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [data, setData] = useState(null);            // mese
  const [trimData, setTrimData] = useState(null);    // [{year,month,data}]
  const [weekData, setWeekData] = useState(null);    // settimana
  const [loading, setLoading] = useState(true);
  const [weekOffset, setWeekOffset] = useState(0);

  const [search, setSearch] = useSearchParams();
  const view = VIEWS.includes(search.get('vista')) ? search.get('vista') : 'mese';
  const setView = (v) => {
    setSearch((prev) => {
      const sp = new URLSearchParams(prev);
      if (v === 'mese') sp.delete('vista');
      else sp.set('vista', v);
      return sp;
    }, { replace: true });
  };

  // ISO Week corrente (con offset)
  const isoWeek = useMemo(() => {
    const d = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
    d.setUTCDate(d.getUTCDate() + 7 * weekOffset);
    const day = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - day);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const wn = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
    return `${d.getUTCFullYear()}-W${String(wn).padStart(2, '0')}`;
  }, [weekOffset]);

  // Mesi del trimestre corrente
  const quarterMonths = useMemo(() => {
    const q = Math.ceil(month / 3);
    const start = (q - 1) * 3 + 1;
    return [start, start + 1, start + 2].map((m) => {
      let yy = year;
      let mm = m;
      if (mm > 12) { mm -= 12; yy += 1; }
      return { year: yy, month: mm };
    });
  }, [year, month]);

  // Fetch della vista corrente
  const load = async () => {
    setLoading(true);
    try {
      if (view === 'mese') {
        const d = await fetchCalendario(year, month);
        setData(d);
      } else if (view === 'trimestre') {
        const months = quarterMonths;
        const all = await Promise.allSettled(months.map((m) => fetchCalendario(m.year, m.month)));
        const failures = all.filter((r) => r.status === 'rejected').length;
        if (failures > 0) toast.warning(`${failures} mese non caricato (riprova)`);
        setTrimData(months.map((m, i) => ({
          ...m,
          data: all[i].status === 'fulfilled' ? all[i].value : { weeks: [], totali: {} },
        })));
      } else if (view === 'settimana') {
        const d = await fetchPeriodo('settimana', isoWeek);
        setWeekData(d);
      }
    } catch {
      toast.error('Errore caricamento calendario');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // `load` cattura year/month/view/isoWeek via closure.
  }, [year, month, view, isoWeek]);

  // Navigation
  const prev = () => {
    if (view === 'settimana') { setWeekOffset((w) => w - 1); return; }
    if (view === 'trimestre') {
      const newMonth = month - 3;
      if (newMonth < 1) { setMonth(newMonth + 12); setYear(year - 1); }
      else setMonth(newMonth);
      return;
    }
    if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1);
  };
  const next = () => {
    if (view === 'settimana') { setWeekOffset((w) => w + 1); return; }
    if (view === 'trimestre') {
      const newMonth = month + 3;
      if (newMonth > 12) { setMonth(newMonth - 12); setYear(year + 1); }
      else setMonth(newMonth);
      return;
    }
    if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1);
  };
  const goToday = () => {
    setYear(today.getFullYear());
    setMonth(today.getMonth() + 1);
    setWeekOffset(0);
  };

  // Helper: se id è "{parent_id}::YYYY-MM-DD", è un'occorrenza virtuale
  // (motore ricorrenze). In quel caso il toggle NON aggiorna il parent
  // (che sballerebbe l'intera serie): fa upsert su `recurrence_overrides`
  // per la sola data specifica via POST /api/recurrence/override.
  const _splitOccurrenceId = (id) => {
    if (typeof id !== 'string' || !id.includes('::')) return null;
    const [parent_id, occurrence_date] = id.split('::');
    if (!parent_id || !/^\d{4}-\d{2}-\d{2}$/.test(occurrence_date || '')) return null;
    return { parent_id, occurrence_date };
  };

  // Toggle MIT con optimistic update granulare nella vista Mese
  const handleToggleMit = async (mitId, currentFatto) => {
    const newVal = !currentFatto;
    setData((prev) => _patchMitInWeeks(prev, mitId, newVal));
    if (newVal && !currentFatto) playCheckSound();
    const occ = _splitOccurrenceId(mitId);
    try {
      if (occ) {
        await setRecurrenceOverride({ parent_kind: 'mit', parent_id: occ.parent_id, occurrence_date: occ.occurrence_date, fatto: newVal });
      } else {
        await toggleMit(mitId, newVal);
      }
    } catch {
      setData((prev) => _patchMitInWeeks(prev, mitId, currentFatto)); // rollback
      toast.error('Salvataggio MIT fallito');
    }
  };

  // Toggle Azione (Mese + Settimana). Reload semplice, no optimistic granulare.
  const handleToggleAzione = async (id, fatto) => {
    const newVal = !fatto;
    const occ = _splitOccurrenceId(id);
    try {
      if (occ) {
        await setRecurrenceOverride({ parent_kind: 'azione', parent_id: occ.parent_id, occurrence_date: occ.occurrence_date, fatto: newVal });
      } else {
        await toggleAzione(id, newVal);
      }
      if (newVal) playCheckSound();
      load();
    } catch {
      toast.error('Salvataggio azione fallito');
    }
  };

  // Riassegnazione MIT (popover + DnD) nella vista Mese
  const reassignMitMese = async (mitId, currentData, newData) => {
    if (!newData || newData === currentData) return;
    try {
      await applyOperations([{ entita: 'mit', azione: 'aggiorna', id: mitId, campi: { data: newData } }]);
      invalidateCache('stats:closed-week');
      await load();
      toast.success(`MIT spostata al ${newData}`, {
        duration: 4000,
        action: {
          label: 'Annulla',
          onClick: async () => {
            try {
              await applyOperations([{ entita: 'mit', azione: 'aggiorna', id: mitId, campi: { data: currentData } }]);
              await load();
              toast.success('Ripristinato');
            } catch (e) {
              toast.error(e?.response?.data?.detail || 'Errore annullamento');
            }
          },
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Spostamento MIT fallito');
      throw e;
    }
  };

  return {
    // state
    year, month, view, isoWeek, todayIso,
    data, trimData, weekData, loading, quarterMonths,
    // nav
    prev, next, goToday, setView,
    // mutations
    handleToggleMit, handleToggleAzione, reassignMitMese,
    load,
  };
}

/** Patch immutabile: aggiorna `m.fatto` per una MIT nel payload Mese, ricalcolando
 * stato/conteggi della cella giorno. Tornare il prev se la MIT non è presente.
 */
function _patchMitInWeeks(prev, mitId, fatto) {
  if (!prev) return prev;
  return {
    ...prev,
    weeks: prev.weeks.map((w) => ({
      ...w,
      giorni: w.giorni.map((g) => {
        if (!g.mit) return g;
        let changed = false;
        const newMit = g.mit.map((m) => {
          if (m.id === mitId) {
            changed = true;
            return { ...m, fatto };
          }
          return m;
        });
        if (!changed) return g;
        const done = newMit.filter((m) => m.fatto).length;
        const tot = newMit.length;
        const stato = tot === 0
          ? 'vuoto'
          : done === 0
            ? 'tutte_da_fare'
            : done < tot
              ? 'parziale'
              : 'tutte_fatte';
        return { ...g, mit: newMit, mit_fatte: done, mit_totale: tot, stato };
      }),
    })),
  };
}
